/**
 * StrategyConfig.tsx - 触达策略配置组件
 * BD配置触达间隔、执行模式、发送时间窗口和LLM设置
 */

import { useState, useEffect } from 'react'
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Button,
  Space,
  message,
  Divider,
  Typography,
  Row,
  Col,
  Alert,
  Tooltip,
  Radio,
  TimePicker,
  Spin,
  Tag,
} from 'antd'
import {
  SaveOutlined,
  ReloadOutlined,
  RobotOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  SettingOutlined,
  ApiOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  QuestionCircleOutlined,
  MailOutlined,
  TwitterOutlined,
  SendOutlined,
} from '@ant-design/icons'
import { userConfigApi } from '../api/client'
import type { UserConfig, UserConfigUpdate } from '../api/client'
import { useAuthStore } from '../store'
import dayjs from 'dayjs'

const { Title, Text, Paragraph } = Typography
const { Option } = Select

// 时间单位类型
type TimeUnit = 'days' | 'hours' | 'minutes'

// 时间单位转换为分钟
const timeUnitToMinutes: Record<TimeUnit, number> = {
  days: 1440,    // 24 * 60
  hours: 60,
  minutes: 1,
}

// 将分钟转换为最合适的单位和值
const minutesToTimeUnit = (minutes: number): { value: number; unit: TimeUnit } => {
  if (minutes % 1440 === 0) {
    return { value: minutes / 1440, unit: 'days' }
  }
  if (minutes % 60 === 0) {
    return { value: minutes / 60, unit: 'hours' }
  }
  return { value: minutes, unit: 'minutes' }
}

// 时区列表
const timezones = [
  { value: 'UTC', label: 'UTC (协调世界时)' },
  { value: 'Asia/Shanghai', label: '中国标准时间 (UTC+8)' },
  { value: 'Asia/Tokyo', label: '日本标准时间 (UTC+9)' },
  { value: 'Asia/Singapore', label: '新加坡时间 (UTC+8)' },
  { value: 'America/New_York', label: '美国东部时间 (UTC-5/-4)' },
  { value: 'America/Los_Angeles', label: '美国太平洋时间 (UTC-8/-7)' },
  { value: 'Europe/London', label: '英国时间 (UTC+0/+1)' },
  { value: 'Europe/Paris', label: '中欧时间 (UTC+1/+2)' },
]

// LLM提供商
const llmProviders = [
  { value: 'openai', label: 'OpenAI', models: ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'gpt-3.5-turbo'] },
  { value: 'anthropic', label: 'Anthropic', models: ['claude-3-opus', 'claude-3-sonnet', 'claude-3-haiku'] },
]

interface StrategyConfigProps {
  onSave?: () => void
}

export function StrategyConfig({ onSave }: StrategyConfigProps) {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null)
  const [userConfig, setUserConfig] = useState<UserConfig | null>(null)
  const [selectedProvider, setSelectedProvider] = useState<string>('openai')
  const { user } = useAuthStore()
  const isAdmin = user?.role === 'admin'

  // 时间单位状态
  const [emailToTwitterUnit, setEmailToTwitterUnit] = useState<TimeUnit>('days')
  const [twitterToTelegramUnit, setTwitterToTelegramUnit] = useState<TimeUnit>('days')
  const [phaseIntervalUnit, setPhaseIntervalUnit] = useState<TimeUnit>('days')

  // 加载配置
  const loadConfig = async () => {
    setLoading(true)
    try {
      const config = await userConfigApi.get()
      setUserConfig(config)
      setSelectedProvider(config.llm_provider || 'openai')

      // 转换分钟为合适的单位
      const emailToTwitter = minutesToTimeUnit(config.email_to_twitter_minutes || 4320)
      const twitterToTelegram = minutesToTimeUnit(config.twitter_to_telegram_minutes || 4320)
      const phaseInterval = minutesToTimeUnit(config.phase_interval_minutes || 4320)

      setEmailToTwitterUnit(emailToTwitter.unit)
      setTwitterToTelegramUnit(twitterToTelegram.unit)
      setPhaseIntervalUnit(phaseInterval.unit)

      form.setFieldsValue({
        // 执行模式
        execution_mode: config.execution_mode || 'manual',

        // 渠道间隔（转换为当前单位的值）
        email_to_twitter: emailToTwitter.value,
        twitter_to_telegram: twitterToTelegram.value,
        phase_interval: phaseInterval.value,

        // 发送时间窗口
        send_window_start: config.send_window_start ? dayjs(config.send_window_start, 'HH:mm') : dayjs('08:00', 'HH:mm'),
        send_window_end: config.send_window_end ? dayjs(config.send_window_end, 'HH:mm') : dayjs('18:00', 'HH:mm'),
        send_window_timezone: config.send_window_timezone || 'UTC',
        skip_weekends: config.skip_weekends !== false,

        // LLM配置
        llm_enabled: config.llm_enabled || false,
        llm_provider: config.llm_provider || 'openai',
        llm_api_key: config.llm_api_key || '',
        llm_model: config.llm_model || 'gpt-4o-mini',

        // Telegram Admin DM 百分比
        telegram_admin_dm_percent: config.telegram_admin_dm_percent || 60,

        // 每日限额
        daily_email_limit: config.daily_email_limit || 50,
        daily_twitter_limit: config.daily_twitter_limit || 20,
        daily_telegram_limit: config.daily_telegram_limit || 10,
      })
    } catch (error: any) {
      message.error('加载配置失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadConfig()
  }, [])

  // 保存配置
  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      setSaving(true)

      // 转换时间值为分钟
      const emailToTwitterMinutes = Math.round(values.email_to_twitter * timeUnitToMinutes[emailToTwitterUnit])
      const twitterToTelegramMinutes = Math.round(values.twitter_to_telegram * timeUnitToMinutes[twitterToTelegramUnit])
      const phaseIntervalMinutes = Math.round(values.phase_interval * timeUnitToMinutes[phaseIntervalUnit])

      const updateData: UserConfigUpdate = {
        execution_mode: values.execution_mode,
        email_to_twitter_minutes: emailToTwitterMinutes,
        twitter_to_telegram_minutes: twitterToTelegramMinutes,
        phase_interval_minutes: phaseIntervalMinutes,
        send_window_start: values.send_window_start?.format('HH:mm'),
        send_window_end: values.send_window_end?.format('HH:mm'),
        send_window_timezone: values.send_window_timezone,
        skip_weekends: values.skip_weekends,
        telegram_admin_dm_percent: values.telegram_admin_dm_percent,
        llm_enabled: values.llm_enabled,
        llm_provider: values.llm_provider,
        llm_api_key: values.llm_api_key,
        llm_model: values.llm_model,
        daily_email_limit: values.daily_email_limit,
        daily_twitter_limit: values.daily_twitter_limit,
        daily_telegram_limit: values.daily_telegram_limit,
      }

      await userConfigApi.update(updateData)
      message.success('配置保存成功')
      onSave?.()
    } catch (error: any) {
      message.error(error.message || '保存失败')
    } finally {
      setSaving(false)
    }
  }

  // 测试LLM连接
  const handleTestLlm = async () => {
    const apiKey = form.getFieldValue('llm_api_key')
    const provider = form.getFieldValue('llm_provider')
    const model = form.getFieldValue('llm_model')

    if (!apiKey) {
      message.warning('请先输入API Key')
      return
    }

    setTesting(true)
    setTestResult(null)
    try {
      const result = await userConfigApi.testLlm({ provider, api_key: apiKey, model })
      setTestResult(result)
      if (result.success) {
        message.success('LLM连接测试成功')
      } else {
        message.error(result.message || 'LLM连接测试失败')
      }
    } catch (error: any) {
      setTestResult({ success: false, message: error.message })
      message.error('测试失败: ' + error.message)
    } finally {
      setTesting(false)
    }
  }

  // 获取当前提供商的模型列表
  const getCurrentModels = () => {
    const provider = llmProviders.find(p => p.value === selectedProvider)
    return provider?.models || []
  }

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>加载配置中...</div>
        </div>
      </Card>
    )
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSave}
    >
      {/* 执行模式 */}
      <Card
        title={
          <Space>
            <ThunderboltOutlined />
            执行模式
          </Space>
        }
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Form.Item
          name="execution_mode"
          label={
            <Space>
              <span>触达执行方式</span>
              <Tooltip title="自动模式会按照配置的间隔自动发送，手动模式需要在任务队列中确认后才发送">
                <QuestionCircleOutlined />
              </Tooltip>
            </Space>
          }
        >
          <Radio.Group>
            <Radio.Button value="manual">
              <Space>
                <SettingOutlined />
                手动执行
              </Space>
            </Radio.Button>
            <Radio.Button value="auto">
              <Space>
                <ThunderboltOutlined />
                自动执行
              </Space>
            </Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Form.Item noStyle shouldUpdate={(prev, curr) => prev.execution_mode !== curr.execution_mode}>
          {({ getFieldValue }) => (
            <Alert
              message={getFieldValue('execution_mode') === 'auto'
                ? '自动模式：系统将按照配置的时间间隔自动发送触达消息'
                : '手动模式：系统会创建待执行任务，需要在任务队列中确认后才发送'}
              type={getFieldValue('execution_mode') === 'auto' ? 'warning' : 'info'}
              showIcon
            />
          )}
        </Form.Item>
      </Card>

      {/* 渠道间隔配置 */}
      <Card
        title={
          <Space>
            <ClockCircleOutlined />
            渠道间隔配置
          </Space>
        }
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
          设置不同渠道之间的触达间隔，支持天、小时、分钟级别的精确控制
        </Paragraph>

        <Row gutter={24}>
          <Col span={8}>
            <Form.Item
              name="email_to_twitter"
              label={
                <Space>
                  <MailOutlined style={{ color: '#1890ff' }} />
                  →
                  <TwitterOutlined style={{ color: '#1DA1F2' }} />
                  邮件到Twitter
                </Space>
              }
              rules={[
                { required: true, message: '请输入间隔时间' },
                { type: 'number', min: 1, message: '时间必须大于0' }
              ]}
            >
              <InputNumber
                min={1}
                style={{ width: '100%' }}
                addonAfter={
                  <Select
                    value={emailToTwitterUnit}
                    onChange={setEmailToTwitterUnit}
                    style={{ width: 80 }}
                  >
                    <Option value="days">天</Option>
                    <Option value="hours">小时</Option>
                    <Option value="minutes">分钟</Option>
                  </Select>
                }
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="twitter_to_telegram"
              label={
                <Space>
                  <TwitterOutlined style={{ color: '#1DA1F2' }} />
                  →
                  <SendOutlined style={{ color: '#0088cc' }} />
                  Twitter到Telegram
                </Space>
              }
              rules={[
                { required: true, message: '请输入间隔时间' },
                { type: 'number', min: 1, message: '时间必须大于0' }
              ]}
            >
              <InputNumber
                min={1}
                style={{ width: '100%' }}
                addonAfter={
                  <Select
                    value={twitterToTelegramUnit}
                    onChange={setTwitterToTelegramUnit}
                    style={{ width: 80 }}
                  >
                    <Option value="days">天</Option>
                    <Option value="hours">小时</Option>
                    <Option value="minutes">分钟</Option>
                  </Select>
                }
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="phase_interval"
              label={
                <Space>
                  同渠道Phase间隔
                  <Tooltip title="同一渠道内Phase 1→2→3的间隔时间">
                    <QuestionCircleOutlined />
                  </Tooltip>
                </Space>
              }
              rules={[
                { required: true, message: '请输入间隔时间' },
                { type: 'number', min: 1, message: '时间必须大于0' }
              ]}
            >
              <InputNumber
                min={1}
                style={{ width: '100%' }}
                addonAfter={
                  <Select
                    value={phaseIntervalUnit}
                    onChange={setPhaseIntervalUnit}
                    style={{ width: 80 }}
                  >
                    <Option value="days">天</Option>
                    <Option value="hours">小时</Option>
                    <Option value="minutes">分钟</Option>
                  </Select>
                }
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* 发送时间窗口 */}
      <Card
        title={
          <Space>
            <ClockCircleOutlined />
            发送时间窗口
          </Space>
        }
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Row gutter={24}>
          <Col span={6}>
            <Form.Item
              name="send_window_start"
              label="开始时间"
              rules={[{ required: true, message: '请选择开始时间' }]}
            >
              <TimePicker format="HH:mm" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="send_window_end"
              label="结束时间"
              rules={[{ required: true, message: '请选择结束时间' }]}
            >
              <TimePicker format="HH:mm" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="send_window_timezone"
              label="时区"
              rules={[{ required: true, message: '请选择时区' }]}
            >
              <Select>
                {timezones.map(tz => (
                  <Option key={tz.value} value={tz.value}>{tz.label}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="skip_weekends"
              label="跳过周末"
              valuePropName="checked"
            >
              <Switch checkedChildren="是" unCheckedChildren="否" />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* Telegram Admin DM 百分比 */}
      <Card
        title={<Space><SendOutlined />Telegram Admin DM 覆盖比例</Space>}
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Form.Item
          name="telegram_admin_dm_percent"
          label="对群里管理员 DM 的百分比"
          extra="例如群里有 10 个管理员，设 60% 则会给其中 6 个发送 DM"
        >
          <InputNumber min={10} max={100} addonAfter="%" style={{ width: 200 }} />
        </Form.Item>
      </Card>

      {/* 每日限额 — Admin 可改，BD 只读提示 */}
      {isAdmin ? (
        <Card
          title={<Space><SettingOutlined />每日发送限额</Space>}
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item name="daily_email_limit" label={<Space><MailOutlined style={{ color: '#1890ff' }} />Email限额</Space>} rules={[{ required: true }]}>
                <InputNumber min={1} max={500} addonAfter="封/天" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="daily_twitter_limit" label={<Space><TwitterOutlined style={{ color: '#1DA1F2' }} />Twitter限额</Space>} rules={[{ required: true }]}>
                <InputNumber min={1} max={200} addonAfter="条/天" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="daily_telegram_limit" label={<Space><SendOutlined style={{ color: '#0088cc' }} />Telegram限额</Space>} rules={[{ required: true }]}>
                <InputNumber min={1} max={100} addonAfter="条/天" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      ) : (
        <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
          <Text type="secondary">每日限额由管理员统一配置：Email 50/天 | Twitter 50/天 | Telegram 30/天</Text>
        </Card>
      )}

      {/* LLM配置 — BD 只有开/关，Admin 可配 Provider/Model/Key */}
      <Card
        title={
          <Space>
            <RobotOutlined />
            AI 个性化
            <Form.Item name="llm_enabled" valuePropName="checked" noStyle>
              <Switch checkedChildren="启用" unCheckedChildren="禁用" />
            </Form.Item>
          </Space>
        }
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Form.Item noStyle shouldUpdate={(prev, curr) => prev.llm_enabled !== curr.llm_enabled}>
          {({ getFieldValue }) => (
            getFieldValue('llm_enabled') ? (
              <>
                {isAdmin ? (
                  <>
                    <Row gutter={24}>
                      <Col span={8}>
                        <Form.Item name="llm_provider" label="AI提供商" rules={[{ required: true }]}>
                          <Select onChange={(value) => setSelectedProvider(value)}>
                            {llmProviders.map(p => (
                              <Option key={p.value} value={p.value}>{p.label}</Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="llm_model" label="模型" rules={[{ required: true }]}>
                          <Select>
                            {getCurrentModels().map(model => (
                              <Option key={model} value={model}>{model}</Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="llm_api_key" label="API Key" rules={[{ required: true }]}>
                          <Input.Password placeholder="sk-..." />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Space>
                      <Button icon={<ApiOutlined />} onClick={handleTestLlm} loading={testing}>测试连接</Button>
                      {testResult && (
                        <Tag color={testResult.success ? 'success' : 'error'} icon={testResult.success ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
                          {testResult.success ? '连接成功' : testResult.message}
                        </Tag>
                      )}
                    </Space>
                  </>
                ) : (
                  <Alert
                    message="AI 个性化已启用"
                    description="系统将使用 AI 优化触达消息内容，使其更加个性化和专业。API 配置由管理员统一管理。"
                    type="success"
                    showIcon
                  />
                )}
              </>
            ) : (
              <Alert
                message="AI 个性化已关闭"
                description="开启后，系统会用 AI 分析项目信息并生成更有针对性的话术。"
                type="info"
                showIcon
              />
            )
          )}
        </Form.Item>
      </Card>

      <Divider />

      {/* 操作按钮 */}
      <Form.Item>
        <Space>
          <Button
            type="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
            loading={saving}
          >
            保存配置
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={loadConfig}
          >
            重新加载
          </Button>
        </Space>
      </Form.Item>
    </Form>
  )
}

// 独立页面包装器
export function StrategyConfigPage() {
  return (
    <Card title="触达策略配置">
      <StrategyConfig />
    </Card>
  )
}

export default StrategyConfig
